import React, { useState } from 'react';
import {
    Modal,
    Button,
    TextInput,
    Textarea,
    Select,
    Group,
    Stack,
    FileInput,
    Text,
    NumberInput,
    Divider,
    Alert,
    ActionIcon,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { DateTimePicker } from '@mantine/dates';
import { t } from 'i18next';
import { IconUpload, IconTrash, IconFile } from '@tabler/icons-react';
import { ILabTest } from '../../../global/entities/LabTest/LabTest';
import { ILabResult } from '../../../global/entities/LabTest/LabResult';
import { usePutLabTestMutations } from '../../../global/entities/LabTest/mutations/usePutLabTestMutations';
import { useGetLabTestUnits } from '../../../global/entities/LabTest/queries/useGetLabTestUnits';
import { LabTestApi } from '../../../global/apis/LabTestApi/LabTestApi';
import { notifications } from '@mantine/notifications';

interface IManualLabResultModalProps {
    opened: boolean;
    onClose: () => void;
    pendingLabTests: ILabTest[];
}

export const ManualLabResultModal: React.FC<IManualLabResultModalProps> = ({
    opened,
    onClose,
    pendingLabTests,
}) => {
    const [selectedTest, setSelectedTest] = useState<ILabTest | null>(null);
    const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
    const [isUploading, setIsUploading] = useState(false);
    const [fileInputValue, setFileInputValue] = useState<File[]>([]);
    const [sessionUploadedFiles, setSessionUploadedFiles] = useState<File[]>([]);
    
    const putLabTestMutation = usePutLabTestMutations();
    const { data: units = [], isLoading: unitsLoading } = useGetLabTestUnits();

    const form = useForm<ILabResult>({
        initialValues: {
            value: 0,
            unitID: 1, // Default unit ID - should be configurable
            description: '',
            interpretation: '',
            analysisDate: new Date(),
        },
        validate: {
            value: (value) => (value <= 0 ? t('formError.valueRequired') : null),
            unitID: (value) => (!value ? t('formError.unitRequired') : null),
            analysisDate: (value) => (!value ? t('formError.dateRequired') : null),
        },
    });

    const handleTestSelection = (testId: string | null) => {
        if (testId) {
            const test = pendingLabTests.find(t => t.id.toString() === testId);
            setSelectedTest(test || null);
        } else 
            setSelectedTest(null);
        
    };

    const handleModalClose = async () => {
        // Clean up any uploaded files when modal is cancelled
        if (sessionUploadedFiles.length > 0 && selectedTest) {
            try {
                const deletePromises = sessionUploadedFiles.map(file => 
                    LabTestApi.deleteFile(selectedTest.id.toString(), file.name)
                );
                await Promise.all(deletePromises);
                
                notifications.show({
                    title: t('fileDeleted'),
                    message: t('uploadedFilesCleanedUp'),
                    color: 'blue',
                });
            } catch (error) {
                console.error('Failed to cleanup uploaded files:', error);
                notifications.show({
                    title: t('warning'),
                    message: t('failedToCleanupFiles'),
                    color: 'orange',
                });
            }
        }

        // Reset all state
        form.reset();
        setSelectedTest(null);
        setUploadedFiles([]);
        setSessionUploadedFiles([]);
        setFileInputValue([]);
        onClose();
    };

    const handleFileUpload = async (files: File[]) => {
        if (!selectedTest || files.length === 0) return;

        setIsUploading(true);
        const successfulUploads: string[] = [];
        const failedUploads: string[] = [];

        try {
            for (const file of files) {
                try {
                    const uploadUrl = await LabTestApi.getUploadUrl(
                        selectedTest.id.toString(),
                        file.name
                    );
                    await LabTestApi.uploadFile(uploadUrl, file);
                    successfulUploads.push(file.name);
                } catch (error) {
                    console.error(`Failed to upload ${file.name}:`, error);
                    failedUploads.push(file.name);
                }
            }

            if (successfulUploads.length > 0) {
                const successfulFiles = files.filter(f => successfulUploads.includes(f.name));
                
                notifications.show({
                    title: t('success.filesUploaded'),
                    message: `${successfulUploads.length} file(s) uploaded successfully`,
                    color: 'green',
                });
                setUploadedFiles(prev => [...prev, ...successfulFiles]);
                setSessionUploadedFiles(prev => [...prev, ...successfulFiles]);
                
                // Clear the file input field
                setFileInputValue([]);
            }

            if (failedUploads.length > 0) {
                notifications.show({
                    title: t('error.uploadFailed'),
                    message: `Failed to upload: ${failedUploads.join(', ')}`,
                    color: 'red',
                });
            }
        } catch {
            notifications.show({
                title: t('error.uploadFailed'),
                message: t('error.general'),
                color: 'red',
            });
        } finally {
            setIsUploading(false);
        }
    };

    const handleSubmit = async (values: ILabResult) => {
        if (!selectedTest) {
            notifications.show({
                title: t('error.noTestSelected'),
                message: t('error.selectTestFirst'),
                color: 'red',
            });
            return;
        }

        try {
            await putLabTestMutation.mutateAsync({
                id: selectedTest.id.toString(),
                labResult: values,
            });

            notifications.show({
                title: t('biomarkers.labResultSaved'),
                message: t('biomarkers.labResultSavedDescription'),
                color: 'green',
            });

            // Reset form and close modal
            form.reset();
            setSelectedTest(null);
            setUploadedFiles([]);
            setSessionUploadedFiles([]); // Clear session files on successful save
            onClose();
        } catch {
            notifications.show({
                title: t('error.saveFailed'),
                message: t('error.general'),
                color: 'red',
            });
        }
    };

    const removeFile = async (index: number) => {
        if (!selectedTest) return;

        const fileToRemove = uploadedFiles[index];
        
        try {
            await LabTestApi.deleteFile(selectedTest.id.toString(), fileToRemove.name);
            
            notifications.show({
                title: t('fileDeleted'),
                message: t('fileDeletedDescription'),
                color: 'green',
            });
            
            setUploadedFiles(prev => prev.filter((_, i) => i !== index));
            setSessionUploadedFiles(prev => prev.filter(f => f.name !== fileToRemove.name));
        } catch (error) {
            console.error('Failed to delete file:', error);
            notifications.show({
                title: t('deleteFailed'),
                message: t('failedToDeleteFile'),
                color: 'red',
            });
        }
    };

    const testOptions = pendingLabTests.map(test => ({
        value: test.id.toString(),
        label: `${test.description} - ${test.specimen} (${t('global.dateOrdered')}: ${new Date(test.dateOrdered).toLocaleDateString()})`,
    }));

    return (
        <Modal
            opened={opened}
            onClose={handleModalClose}
            title={t('modal.manualLabResult.title')}
            size="lg"
            centered
        >
            <form onSubmit={form.onSubmit(handleSubmit)}>
                <Stack gap="md">
                    {/* Test Selection */}
                    <Select
                        label={t('modal.manualLabResult.selectTest')}
                        placeholder={t('modal.manualLabResult.selectTestPlaceholder')}
                        data={testOptions}
                        value={selectedTest?.id.toString() || null}
                        onChange={handleTestSelection}
                        required
                        searchable
                    />

                    {selectedTest && (
                        <>
                            {/* Test Information Display */}
                            <Alert color="blue" icon={<IconFile />}>
                                <Text size="sm">
                                    <strong>{t('modal.manualLabResult.test')}:</strong> {selectedTest.description}<br />
                                    <strong>{t('global.specimen')}:</strong> {selectedTest.specimen}<br />
                                    <strong>{t('global.dateOrdered')}:</strong> {new Date(selectedTest.dateOrdered).toLocaleDateString()}
                                </Text>
                            </Alert>

                            <Divider />

                            {/* Lab Result Form */}
                            <Group align="flex-end" gap="md">
                                <NumberInput
                                    label={t('modal.manualLabResult.value')}
                                    placeholder={t('modal.manualLabResult.valuePlaceholder')}
                                    required
                                    min={0}
                                    decimalScale={2}
                                    hideControls
                                    flex={2}
                                    {...form.getInputProps('value')}
                                />

                                <Select
                                    label={t('modal.manualLabResult.unit')}
                                    placeholder={t('modal.manualLabResult.unitPlaceholder')}
                                    data={(units || []).map((unit) => ({
                                        value: unit.id.toString(),
                                        label: unit.name,
                                    }))}
                                    value={form.values.unitID ? form.values.unitID.toString() : null}
                                    onChange={(value) => form.setFieldValue('unitID', value ? Number(value) : 0)}
                                    required
                                    disabled={unitsLoading}
                                    searchable
                                    flex={1}
                                />
                            </Group>

                            <TextInput
                                label={t('modal.manualLabResult.description')}
                                placeholder={t('modal.manualLabResult.descriptionPlaceholder')}
                                {...form.getInputProps('description')}
                            />

                            <Textarea
                                label={t('modal.manualLabResult.interpretation')}
                                placeholder={t('modal.manualLabResult.interpretationPlaceholder')}
                                rows={3}
                                {...form.getInputProps('interpretation')}
                            />

                            <DateTimePicker
                                label={t('modal.manualLabResult.analysisDate')}
                                placeholder={t('modal.manualLabResult.analysisDatePlaceholder')}
                                required
                                {...form.getInputProps('analysisDate')}
                            />

                            <Divider />

                            {/* File Upload Section */}
                            <Stack gap="sm">
                                <Text size="sm" fw={500}>
                                    {t('modal.manualLabResult.attachments')}
                                </Text>
                                
                                <FileInput
                                    label={t('modal.manualLabResult.uploadFiles')}
                                    placeholder={t('modal.manualLabResult.uploadFilesPlaceholder')}
                                    multiple
                                    accept=".pdf,.txt,.jpeg,.jpg,.png"
                                    leftSection={<IconUpload size={14} />}
                                    value={fileInputValue}
                                    onChange={(files) => {
                                        setFileInputValue(files || []);
                                        if (files && files.length > 0) 
                                            handleFileUpload(files);
                                        
                                    }}
                                    disabled={isUploading}
                                />

                                {uploadedFiles.length > 0 && (
                                    <Stack gap="xs">
                                        <Text size="sm" c="dimmed">
                                            {t('modal.manualLabResult.uploadedFiles')}:
                                        </Text>
                                        {uploadedFiles.map((file, index) => (
                                            <Group key={index} justify="space-between">
                                                <Group gap="xs">
                                                    <IconFile size={16} />
                                                    <Text size="sm">{file.name}</Text>
                                                </Group>
                                                <ActionIcon
                                                    size="sm"
                                                    color="red"
                                                    onClick={() => removeFile(index)}
                                                >
                                                    <IconTrash size={14} />
                                                </ActionIcon>
                                            </Group>
                                        ))}
                                    </Stack>
                                )}
                            </Stack>
                        </>
                    )}

                    {/* Action Buttons */}
                    <Group justify="flex-end" mt="md">
                        <Button variant="light" onClick={handleModalClose}>
                            {t('button.cancel')}
                        </Button>
                        <Button
                            type="submit"
                            loading={putLabTestMutation.isPending}
                            disabled={!selectedTest}
                        >
                            {t('button.save')}
                        </Button>
                    </Group>
                </Stack>
            </form>
        </Modal>
    );
}; 